/**
 * Connectivity Center — connect Parallax to any MAVLink drone or radio at runtime.
 *
 * The registry always supported this; the UI never exposed it. Presets cover the
 * links an operator actually meets in the field — a SiK/RFD telemetry radio, a
 * SITL or autopilot TCP bridge, a serial/USB link, and a UDP feed — and the
 * helper spells out the one thing that trips people up at the edge: putting the
 * laptop on the radio's subnet.
 *
 * Honest about the container boundary: a backend running in a container can only
 * reach links the host exposes to it. The USB / RADIO tab is the way around it
 * that needs nothing installed — the browser opens the COM port itself and this
 * page carries the bytes — and the host-side Parallax Bridge remains the answer
 * for browsers with no Web Serial and for "change your PC's IP to match the
 * radio" cases.
 */

import {
  useCallback,
  useEffect,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import {
  addConnection,
  adoptDiscovered,
  getPortOffers,
  listDiscovered,
  type Discovery,
  dropConnection,
  listConnections,
  type Connection,
  type PortOffer,
} from '@/api/connections';
import { useCanCommand } from '@/stores/authStore';

import {
  BAUD_RATES,
  describe as describeBridgeError,
  serialBridgeState,
  startSerialBridge,
  stopSerialBridge,
  subscribeSerialBridge,
} from './serialBridgeClient';
import { MeshPanel } from './MeshPanel';
import { probeSummary, webSerialSupported } from './webSerialBridge';

const OK = 'oklch(0.78 0.15 155)';
const WARN = 'oklch(0.80 0.15 85)';
const DANGER = 'oklch(0.72 0.19 28)';

interface Preset {
  key: string;
  label: string;
  template: string;
  sysid: number;
  help: ReactNode;
}

const PRESETS: Preset[] = [
  {
    key: 'sik',
    label: 'SiK / RFD radio',
    template: 'udpin:0.0.0.0:14550',
    sysid: 1,
    help: (
      <>
        Ground radio on USB, forwarded to UDP by the host bridge (or a GCS). Parallax listens on the
        port. If the radio hands out its own IP, match your laptop to its subnet first — see the IP
        helper below.
      </>
    ),
  },
  {
    key: 'sitl',
    label: 'ArduPilot SITL',
    template: 'udpin:0.0.0.0:14550',
    sysid: 1,
    help: (
      <>
        A software-in-the-loop autopilot — the real ArduPilot/PX4 firmware, no airframe. Launch it
        with <span className="font-mono">--out=udp:HOST:14550</span> pointed at this host and
        Parallax flies it exactly like a real aircraft. For SITL over TCP use the option below.
      </>
    ),
  },
  {
    key: 'tcpin',
    label: 'TCP in (forwarded)',
    template: 'tcpin:0.0.0.0:5770',
    sysid: 1,
    help: (
      <>
        Parallax listens and the ground station connects in: Mission Planner, QGroundControl or a
        mavlink-router forwarding a TCP link to this host and port.
      </>
    ),
  },
  {
    key: 'tcp',
    label: 'TCP (SITL / bridge)',
    template: 'tcp:127.0.0.1:5760',
    sysid: 1,
    help: <>SITL, mavlink-router, or an autopilot exposed over TCP. Point it at host:port.</>,
  },
  {
    key: 'serial',
    label: 'Serial / COM',
    template: 'serial:COM3:57600',
    sysid: 1,
    help: (
      <>
        A radio or autopilot on a COM port / <span className="font-mono">/dev/ttyUSB0</span> that the
        <b> backend host</b> can see. For a radio plugged into <i>this</i> laptop, use the{' '}
        <b>USB / RADIO</b> tab — it needs nothing installed. On a browser without Web Serial, run the{' '}
        <b>Parallax Bridge</b> here and it forwards the serial link over UDP.
      </>
    ),
  },
  {
    key: 'udpout',
    label: 'UDP out (IP drone)',
    template: 'udpout:192.168.1.10:14550',
    sysid: 1,
    help: (
      <>
        An IP-based autopilot/radio you dial out to. Your laptop must be on the same subnet as the
        device (e.g. <span className="font-mono">192.168.1.x</span>).
      </>
    ),
  },
];

/** The transport a listening template opens, or null for a link that dials out. */
function listenTransport(spec: string): 'udp' | 'tcp' | null {
  const scheme = spec.trim().split(':', 1)[0]?.toLowerCase();
  if (scheme === 'udpin') return 'udp';
  if (scheme === 'tcpin') return 'tcp';
  return null;
}

function blockText(offer: PortOffer): string {
  return `${offer.transport.toUpperCase()} ${offer.block.start}-${offer.block.end}`;
}

/**
 * What the dialog says about the port it is offering. Said in words rather
 * than left as a bare number, because the number decides which team the
 * aircraft joins, and 14550 used to belong to whichever team held the bottom
 * of the range.
 */
function offerNote(offers: PortOffer[] | null, transport: 'udp' | 'tcp' | null): string | null {
  if (offers === null || transport === null) return null;
  const mine = offers.filter((o) => o.transport === transport);
  const free = mine.find((o) => o.port !== null);
  if (free && free.port !== null) {
    return (
      `Port ${free.port} is the first free one in ${free.callsign}'s ${blockText(free)} block. ` +
      `The aircraft joins ${free.callsign} as ${free.droneId ?? 'a team aircraft'}.`
    );
  }
  if (mine.length) {
    return (
      `Every port in ${mine.map((o) => `${o.callsign}'s ${blockText(o)}`).join(' and ')} ` +
      'already carries an aircraft. Drop one, or ask for a wider block.'
    );
  }
  return (
    `Your team has no ${transport.toUpperCase()} port block, so a link here joins no team ` +
    'until an admin assigns it.'
  );
}

function stateColor(state: string): string {
  if (state === 'connected') return OK;
  if (state === 'connecting') return WARN;
  return DANGER;
}

export function ConnectDialog({ onClose }: { onClose: () => void }): ReactNode {
  const canCommand = useCanCommand();
  const [connections, setConnections] = useState<Connection[]>([]);
  const [preset, setPreset] = useState<Preset>(PRESETS[0]!);
  const [droneId, setDroneId] = useState('');
  const [callsign, setCallsign] = useState('');
  const [connectionString, setConnectionString] = useState(PRESETS[0]!.template);
  const [sysid, setSysid] = useState('1');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ text: string; warn: boolean } | null>(null);
  const [showHelper, setShowHelper] = useState(false);
  const [usbTab, setUsbTab] = useState(false);
  const [meshTab, setMeshTab] = useState(false);

  const [discovery, setDiscovery] = useState<Discovery | null>(null);
  // Null until loaded, so "no block" is never said about a team whose blocks
  // simply have not arrived yet.
  const [offers, setOffers] = useState<PortOffer[] | null>(null);
  // Once the operator types a string of their own, a refreshed offer must not
  // overwrite it.
  const [edited, setEdited] = useState(false);

  const refresh = useCallback(async () => {
    const result = await listConnections();
    if (result.ok) setConnections(result.data);
    // Aircraft announce themselves continuously, so this list is only true for
    // as long as they keep talking — refreshed on the same beat as the links.
    const heard = await listDiscovered();
    if (heard.ok) setDiscovery(heard.data);
  }, []);

  const adopt = async (key: string, sysid: number) => {
    setBusy(true);
    const result = await adoptDiscovered({ key, droneId: `SYS-${sysid}`, callsign: `SYS ${sysid}` });
    setBusy(false);
    setMessage(
      result.ok
        ? { text: `Adopted SYS ${sysid} — link opening`, warn: false }
        : { text: result.error.message, warn: true },
    );
    void refresh();
  };

  const loadOffers = useCallback(async () => {
    const result = await getPortOffers();
    // A viewer is refused the offer outright; the form is not shown to one.
    setOffers(result.ok ? result.data : []);
  }, []);

  useEffect(() => {
    void refresh();
    void loadOffers();
    // Reflect a link coming up (connecting → connected) while the dialog is open.
    const timer = setInterval(() => void refresh(), 2000);
    return () => clearInterval(timer);
  }, [refresh, loadOffers]);

  /** The template, with the port swapped for a free one in the operator's own block. */
  const templateFor = useCallback(
    (next: Preset): string => {
      const transport = listenTransport(next.template);
      const offer = offers?.find((o) => o.transport === transport && o.connectionString);
      return offer?.connectionString ?? next.template;
    },
    [offers],
  );

  useEffect(() => {
    if (!edited) setConnectionString(templateFor(preset));
  }, [edited, preset, templateFor]);

  const pickPreset = (next: Preset) => {
    setUsbTab(false);
    setMeshTab(false);
    setPreset(next);
    setEdited(false);
    setConnectionString(templateFor(next));
    setSysid(String(next.sysid));
    setMessage(null);
  };

  const transport = listenTransport(connectionString);
  const note = offerNote(offers, transport);

  const connect = async () => {
    if (!connectionString.trim()) {
      setMessage({ text: 'A connection string is required.', warn: true });
      return;
    }
    setBusy(true);
    // Drone id and callsign may be left blank on a team's port block: the
    // server names the aircraft for the team. Anywhere else it says so.
    const result = await addConnection({
      ...(droneId.trim() ? { droneId: droneId.trim() } : {}),
      ...(callsign.trim() ? { callsign: callsign.trim() } : {}),
      connectionString: connectionString.trim(),
      sysid: Number.parseInt(sysid, 10) || 1,
    });
    setBusy(false);
    if (result.ok) {
      setMessage({ text: `Link opened. ${result.data.droneId} is ${result.data.state}.`, warn: false });
      setDroneId('');
      setCallsign('');
      setEdited(false);
      void refresh();
      void loadOffers();
    } else {
      setMessage({ text: result.error.message, warn: true });
    }
  };

  const real = connections.filter((c) => !c.simulated);

  // Portal to <body>: this dialog is opened from inside panels and phone
  // sheets whose transforms would otherwise capture `fixed` positioning and
  // render the modal nested inside the sheet instead of over the app.
  return createPortal(
    <div
      className="pointer-events-auto fixed inset-0 z-[60] flex items-center justify-center p-6 phone:p-3"
      style={{ background: 'rgba(6,6,8,0.55)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
    >
      <div
        className="connect-dialog glass scroll-thin flex max-h-full w-[min(560px,100%)] flex-col overflow-y-auto rounded-[22px] p-6 phone:p-4"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-center gap-2.5">
          <span className="text-[15px]" style={{ color: OK }}>
            ⇄
          </span>
          <span className="label-mono text-[12px]">CONNECT A DRONE</span>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto font-mono text-[10px] text-white/45 hover:text-white"
          >
            ESC
          </button>
        </div>

        {!canCommand ? (
          <p className="text-[12px] text-muted-foreground">
            Connecting aircraft needs operator access.
          </p>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap gap-1.5">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => pickPreset(p)}
                  className="rounded-full border px-3 py-[6px] font-mono text-[9.5px] font-semibold"
                  style={pillStyle(!usbTab && !meshTab && preset.key === p.key)}
                >
                  {p.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => {
                  setUsbTab(true);
                  setMeshTab(false);
                }}
                className="rounded-full border px-3 py-[6px] font-mono text-[9.5px] font-semibold"
                style={pillStyle(usbTab)}
              >
                USB / RADIO
              </button>
              <button
                type="button"
                onClick={() => {
                  setMeshTab(true);
                  setUsbTab(false);
                }}
                className="rounded-full border px-3 py-[6px] font-mono text-[9.5px] font-semibold"
                style={pillStyle(meshTab)}
              >
                MESH RADIO
              </button>
            </div>

            {meshTab ? (
              <MeshPanel />
            ) : usbTab ? (
              <UsbRadioPanel
                droneId={droneId}
                callsign={callsign}
                onDroneId={setDroneId}
                onCallsign={setCallsign}
                onConnected={() => void refresh()}
              />
            ) : (
              <>
                <p className="mb-3 text-[10.5px] leading-relaxed text-white/55">{preset.help}</p>

                <div className="flex flex-col gap-2">
                  <div className="grid grid-cols-2 gap-2">
                    <Field
                      label="DRONE ID"
                      value={droneId}
                      onChange={setDroneId}
                      placeholder={offerNameFor(offers, transport) ?? 'VIPER-07'}
                    />
                    <Field
                      label="CALLSIGN"
                      value={callsign}
                      onChange={setCallsign}
                      placeholder={offerNameFor(offers, transport) ?? 'Viper 7'}
                    />
                  </div>
                  <Field
                    label="CONNECTION STRING"
                    value={connectionString}
                    onChange={(value) => {
                      setEdited(true);
                      setConnectionString(value);
                    }}
                    placeholder="udpin:0.0.0.0:14550"
                    mono
                  />
                  {note ? (
                    <p className="text-[10.5px] leading-relaxed text-white/55" data-testid="port-offer">
                      {note}
                    </p>
                  ) : null}
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="SYS ID" value={sysid} onChange={setSysid} placeholder="1" />
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={() => void connect()}
                        disabled={busy}
                        className="h-9 w-full rounded-full bg-white/90 font-mono text-[10px] font-bold tracking-wide text-[#0c0c0d] disabled:opacity-40"
                      >
                        {busy ? 'OPENING…' : 'CONNECT'}
                      </button>
                    </div>
                  </div>
                </div>

                {message ? (
                  <div
                    className="mt-2.5 font-mono text-[10.5px]"
                    style={{ color: message.warn ? WARN : OK }}
                  >
                    {message.text}
                  </div>
                ) : null}
              </>
            )}

            <button
              type="button"
              onClick={() => setShowHelper((v) => !v)}
              className="mt-3 text-left font-mono text-[9.5px] font-semibold text-white/45 hover:text-white/70"
            >
              {showHelper ? '▾' : '▸'} IP / SUBNET HELPER — matching your laptop to a radio
            </button>
            {showHelper ? (
              <div className="mt-2 flex flex-col gap-2 rounded-[12px] border border-white/8 bg-black/25 p-3 text-[10.5px] leading-relaxed text-white/60">
                <p>
                  Many IP radios/autopilots ship on a fixed subnet (often{' '}
                  <span className="font-mono">192.168.144.x</span> or{' '}
                  <span className="font-mono">192.168.1.x</span>). Put this laptop on that subnet, then
                  use the device's IP in the connection string.
                </p>
                <div className="font-mono text-[9.5px] text-white/50">
                  <div className="text-white/40">Windows (run as admin):</div>
                  netsh interface ip set address "Ethernet" static 192.168.144.50 255.255.255.0
                </div>
                <div className="font-mono text-[9.5px] text-white/50">
                  <div className="text-white/40">Linux/macOS:</div>
                  sudo ip addr add 192.168.144.50/24 dev eth0
                </div>
                <p className="text-white/45">
                  Serial/COM and subnet-matched IP links are opened by the <b>Parallax Bridge</b> on
                  this laptop, which forwards them to the backend over UDP.
                </p>
              </div>
            ) : null}

            {discovery?.listening ? (
              <>
                <div className="mt-4 mb-1 flex items-center gap-2 label-mono text-[9px]">
                  <span>HEARD ON THE NETWORK</span>
                  <span className="text-[color:var(--muted-foreground-3)]">
                    port {discovery.port}
                  </span>
                </div>
                {discovery.candidates.length === 0 ? (
                  <p className="text-[11px] text-[color:var(--muted-foreground-3)]">
                    Nothing transmitting. An aircraft pointed at this port appears here on its
                    own — no connection string needed.
                  </p>
                ) : (
                  <div className="flex flex-col gap-1">
                    {discovery.candidates.map((c) => (
                      <div
                        key={c.key}
                        className="well flex items-center gap-2 rounded-[10px] px-2.5 py-1.5 text-[11px]"
                      >
                        <span className="tnum font-semibold">SYS {c.sysid}</span>
                        <span className="text-[color:var(--muted-foreground-3)]">{c.address}</span>
                        <span className="tnum ml-auto text-[10px] text-[color:var(--muted-foreground-3)]">
                          {c.heartbeats} beats · {c.ageS.toFixed(0)}s ago
                        </span>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void adopt(c.key, c.sysid)}
                          className="rounded-full border border-[color:var(--border)] px-2.5 py-1 label-mono text-[8.5px] disabled:opacity-40"
                        >
                          ADOPT
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : null}

            <div className="mt-4 mb-1 label-mono text-[9px]">ACTIVE LINKS</div>
            {real.length === 0 ? (
              <p className="text-[10.5px] text-white/40">
                No real links yet. Simulated aircraft aren't listed here.
              </p>
            ) : (
              <div className="flex flex-col gap-1.5">
                {real.map((c) => (
                  <div
                    key={c.droneId}
                    className="flex items-center gap-2.5 rounded-[12px] border border-white/8 bg-white/5 px-3 py-2"
                  >
                    <span
                      className="size-2 flex-none rounded-full"
                      style={{ background: stateColor(c.state), boxShadow: `0 0 6px ${stateColor(c.state)}` }}
                    />
                    <span className="font-mono text-[11px] font-semibold">{c.droneId}</span>
                    <span className="font-mono text-[9.5px] text-white/45">{c.link}</span>
                    <span className="ml-auto font-mono text-[8.5px] uppercase" style={{ color: stateColor(c.state) }}>
                      {c.state}
                    </span>
                    <button
                      type="button"
                      onClick={() => void dropConnection(c.droneId).then(() => void refresh())}
                      className="rounded-full border px-2 py-[3px] font-mono text-[8.5px] font-bold"
                      style={{ color: DANGER, borderColor: `color-mix(in oklch, ${DANGER} 50%, transparent)` }}
                    >
                      DROP
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

/** The name the server will give an aircraft on the offered port, for the empty field. */
function offerNameFor(offers: PortOffer[] | null, transport: 'udp' | 'tcp' | null): string | null {
  if (!offers || !transport) return null;
  return offers.find((o) => o.transport === transport && o.droneId)?.droneId ?? null;
}

function pillStyle(selected: boolean): CSSProperties {
  return {
    background: selected ? 'rgba(255,255,255,0.92)' : 'transparent',
    color: selected ? '#0c0c0d' : 'rgba(244,244,245,0.6)',
    borderColor: selected ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.14)',
  };
}

const VERDICT_COLOR: Record<string, string> = { found: OK, noise: WARN, silent: WARN };

/**
 * A radio plugged into this laptop, with nothing installed.
 *
 * Web Serial is Chromium-only, and the honest thing to do on Firefox or Safari
 * is to say so and name the fallback — a disabled button that never explains
 * itself sends the operator hunting through settings for a permission that does
 * not exist in their browser.
 */
function UsbRadioPanel({
  droneId,
  callsign,
  onDroneId,
  onCallsign,
  onConnected,
}: {
  droneId: string;
  callsign: string;
  onDroneId: (value: string) => void;
  onCallsign: (value: string) => void;
  onConnected: () => void;
}): ReactNode {
  const bridge = useSyncExternalStore(subscribeSerialBridge, serialBridgeState);
  const [baudRate, setBaudRate] = useState<number>(BAUD_RATES[0]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!webSerialSupported()) {
    return (
      <div className="flex flex-col gap-2 rounded-[12px] border border-white/8 bg-black/25 p-3 text-[10.5px] leading-relaxed text-white/60">
        <p>
          <b>This browser cannot open a serial port.</b> Web Serial is a Chromium API — Chrome or
          Edge on a desktop can hand this page your USB radio; Firefox and Safari have not shipped
          it.
        </p>
        <p>
          Either open Parallax in Chrome or Edge, or run the <b>Parallax Bridge</b> on this laptop
          and connect with the <b>Serial / COM</b> preset, which forwards the radio over UDP.
        </p>
      </div>
    );
  }

  const start = async () => {
    if (!droneId.trim() || !callsign.trim()) {
      setError('Drone id and callsign are both required.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await startSerialBridge({
        droneId: droneId.trim(),
        callsign: callsign.trim(),
        sysId: 1,
        baudRate,
      });
      onConnected();
    } catch (failure) {
      setError(describeBridgeError(failure));
    }
    setBusy(false);
  };

  return (
    <div className="flex flex-col gap-2">
      <p className="text-[10.5px] leading-relaxed text-white/55">
        Plug an RFD900, a SiK radio or an autopilot into this laptop's USB. The browser asks which
        port; Parallax carries the MAVLink both ways. Nothing to install.
      </p>

      <div className="grid grid-cols-2 gap-2">
        <Field label="DRONE ID" value={droneId} onChange={onDroneId} placeholder="VIPER-07" />
        <Field label="CALLSIGN" value={callsign} onChange={onCallsign} placeholder="Viper 7" />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-1">
          <span className="label-mono text-[8px]">BAUD</span>
          <select
            value={baudRate}
            onChange={(event) => setBaudRate(Number.parseInt(event.target.value, 10))}
            disabled={bridge.running}
            className="well h-9 rounded-[10px] px-2 font-mono text-[12px] outline-none disabled:opacity-40"
          >
            {BAUD_RATES.map((rate) => (
              <option key={rate} value={rate}>
                {rate}
                {rate === 57600 ? ' (SiK / RFD default)' : ''}
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-end">
          {bridge.running ? (
            <button
              type="button"
              onClick={() => void stopSerialBridge()}
              className="h-9 w-full rounded-full border font-mono text-[10px] font-bold tracking-wide"
              style={{ color: DANGER, borderColor: `color-mix(in oklch, ${DANGER} 50%, transparent)` }}
            >
              DISCONNECT RADIO
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void start()}
              disabled={busy}
              className="h-9 w-full rounded-full bg-white/90 font-mono text-[10px] font-bold tracking-wide text-[#0c0c0d] disabled:opacity-40"
            >
              {busy ? 'OPENING…' : 'CHOOSE PORT'}
            </button>
          )}
        </div>
      </div>

      {bridge.running ? (
        <div className="flex flex-col gap-1 rounded-[12px] border border-white/8 bg-black/25 p-3">
          <div className="font-mono text-[11px] font-semibold">{bridge.label}</div>
          <div className="tnum font-mono text-[9.5px] text-white/45">
            {bridge.status.bytes} bytes · {bridge.status.frames} frames · {bridge.baudRate} baud
          </div>
          <div
            className="font-mono text-[10.5px]"
            style={{ color: VERDICT_COLOR[bridge.status.verdict] ?? WARN }}
          >
            {probeSummary(bridge.status)}
          </div>
        </div>
      ) : null}

      {error ?? bridge.error ? (
        <div className="font-mono text-[10.5px]" style={{ color: WARN }}>
          {error ?? bridge.error}
        </div>
      ) : null}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  mono,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  mono?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="label-mono text-[8px]">{label}</span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className={`well h-9 rounded-[10px] px-3 text-[12px] outline-none ${mono ? 'font-mono' : ''}`}
      />
    </label>
  );
}

export default ConnectDialog;
